import type { ComponentPropsWithoutRef, ReactNode } from "react";
import { Search } from "lucide-react";

import { cn } from "@/lib/utils";

type SearchInputProps = Omit<ComponentPropsWithoutRef<"input">, "type"> & {
  readonly leading?: ReactNode;
  readonly onSearch?: () => void;
  readonly searchButtonLabel?: string;
  readonly searchButtonDisabled?: boolean;
  readonly showSearchButton?: boolean;
  readonly showSearchIcon?: boolean;
  readonly containerClassName?: string;
  readonly inputClassName?: string;
};

export function SearchInput({
  leading,
  onSearch,
  searchButtonLabel = "검색",
  searchButtonDisabled = false,
  showSearchButton = true,
  showSearchIcon = true,
  containerClassName,
  inputClassName,
  className,
  disabled,
  ...inputProps
}: SearchInputProps) {
  const hasLeading = leading != null;

  return (
    <div
      className={cn(
        "group/search-input flex h-10 items-center overflow-hidden rounded-full border border-[#D6D6DE] bg-white text-[#333333] transition-colors",
        "focus-within:border-[#0E59C6] focus-within:ring-2 focus-within:ring-[#0E59C6]/15",
        disabled && "opacity-50",
        hasLeading ? "w-full" : "w-64",
        containerClassName,
        className
      )}
    >
      {hasLeading && (
        <>
          <div className="flex h-full shrink-0 items-center">{leading}</div>
          <div className="h-5 w-px shrink-0 bg-[#E5E7EB]" />
        </>
      )}
      <div className="relative min-w-0 flex-1">
        <input
          {...inputProps}
          type="text"
          disabled={disabled}
          className={cn(
            "h-10 w-full border-0 bg-transparent py-2 pl-4 font-pretendard text-sm text-[#333333] outline-none placeholder:text-[#939399]",
            showSearchButton ? "pr-10" : "pr-4",
            hasLeading ? "pl-4" : "pl-5",
            inputClassName
          )}
        />
        {showSearchButton ? (
          <button
            type="button"
            aria-label={searchButtonLabel}
            onClick={onSearch}
            disabled={disabled || searchButtonDisabled}
            className="absolute right-3 top-1/2 flex size-6 -translate-y-1/2 items-center justify-center rounded-full text-[#939399] transition-colors hover:text-[#333333] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0E59C6]/30 disabled:pointer-events-none disabled:opacity-40"
          >
            <Search className="size-4" aria-hidden="true" />
          </button>
        ) : showSearchIcon ? (
          <Search
            className="pointer-events-none absolute right-3 top-1/2 size-4 -translate-y-1/2 text-[#939399]"
            aria-hidden="true"
          />
        ) : null}
      </div>
    </div>
  );
}
